import { Close } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";

import DatasetSelectorContent from "./DatasetSelectorContent";

interface DatasetSelectorModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedDatasets: any[]) => void;
  defaultSelectedDatasets?: any[];
}

/**
 * DatasetSelectorModal - A modal dialog for selecting datasets.
 *
 * Thin wrapper around DatasetSelectorContent that adds Dialog chrome and the
 * staged OK/Cancel behavior (selection is held locally and only committed via
 * onConfirm). The folder-aware picker body lives in DatasetSelectorContent so
 * it can also be embedded inline in the agent forms.
 */
const DatasetSelectorModal = ({
  open,
  onClose,
  onConfirm,
  defaultSelectedDatasets = [],
}: DatasetSelectorModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [tempSelected, setTempSelected] = useState<any[]>(
    defaultSelectedDatasets,
  );

  // Seed the staged selection from defaults whenever the modal (re)opens.
  useEffect(() => {
    setTempSelected(defaultSelectedDatasets ?? []);
  }, [open, defaultSelectedDatasets]);

  const handleConfirm = () => {
    onConfirm(tempSelected);
    onClose();
  };

  const handleCancel = () => {
    // Revert any staged changes back to the defaults.
    setTempSelected(defaultSelectedDatasets ?? []);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      data-testid="aa-ds-selector-modal"
      data-attr="assistant:modal:dataset-selector"
      PaperProps={{
        sx: {
          height: isMobile ? "100vh" : "80vh",
          maxHeight: isMobile ? "100vh" : "800px",
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" fontWeight="bold">
            Select Datasets
          </Typography>
          <IconButton
            onClick={handleCancel}
            size="small"
            data-attr="assistant:btn:close-dataset-selector"
          >
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        <DatasetSelectorContent
          selectedDatasets={tempSelected}
          onSelectionChange={setTempSelected}
          showChips={false}
        />
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          p: 2,
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 1 : 0,
        }}
      >
        <Button
          onClick={handleCancel}
          color="inherit"
          fullWidth={isMobile}
          data-testid="aa-ds-selector-cancel"
          data-attr="assistant:btn:cancel-dataset-selection"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          fullWidth={isMobile}
          data-testid="aa-ds-selector-confirm"
          data-attr="assistant:btn:confirm-dataset-selection"
        >
          OK ({tempSelected.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DatasetSelectorModal;
